import { Badge, Dropdown, List, Button } from "antd";
import { BellOutlined } from "@ant-design/icons";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { NotificationList } from "./NotificationList";
import { NotificationType } from "./notificationTypes";
import { getNotifications, markNotificationAsRead } from '../../api/api';
import { ROUTES } from '../common';
import { useUser } from '../../context';

type Notification = {
  _id: string;
  userId: string;
  type: NotificationType;
  message: string;
  read: boolean;
  createdAt: string;
  data?: any;
};

export const NotificationBell = () => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [visible, setVisible] = useState(false);
  const [lastNotificationCount, setLastNotificationCount] = useState(0);
  const navigate = useNavigate();
  const { user } = useUser();

  const fetchNotifications = () => {
    getNotifications().then(data => {
      const notificationArray = Array.isArray(data) ? data : [];
      setNotifications(notificationArray);
      
      // Check for new chat notifications and dispatch badge updates
      const chatNotifications = notificationArray.filter(n => n.type === 'chat_message' && !n.read);
      if (chatNotifications.length > lastNotificationCount) {
        // New chat notifications detected, dispatch badge update events
        chatNotifications.forEach(notification => {
          const data = notification.data || {};
          if (data.jobApplicationId && data.chatType && data.messageCount) {
            console.log('🔔 Dispatching badge update for:', data.jobApplicationId, 'count:', data.messageCount);
            const badgeEvent = new CustomEvent('chatNotificationReceived', {
              detail: {
                chatType: data.chatType,
                jobApplicationId: data.jobApplicationId,
                messageCount: data.messageCount,
                notificationId: notification._id
              }
            });
            window.dispatchEvent(badgeEvent);
          }
        });
      }
      setLastNotificationCount(chatNotifications.length);
    });
  };

  useEffect(() => {
    fetchNotifications(); // Optionally fetch on mount
    
    // Poll for new notifications every 10 seconds to update badges
    const interval = setInterval(fetchNotifications, 10000);
    return () => clearInterval(interval);
  }, [lastNotificationCount]);

  const handleOpenChange = (open: boolean) => {
    setVisible(open);
    if (open) {
      fetchNotifications(); // Fetch every time dropdown opens
    }
  };

  const unreadCount = notifications.filter(n => !n.read).length;

  const markAsRead = async (id: string) => {
    console.log("markAsRead called with id:", id, typeof id);
    await markNotificationAsRead(id);
    setNotifications(notifications =>
      notifications.map(n => n._id === id ? { ...n, read: true } : n)
    );
  };

  const handleNotificationClick = (notification: Notification) => {
    console.log("Notification clicked:", notification);
    
    // Handle chat messages separately (they need special navigation logic)
    if (notification.type === NotificationType.CHAT_MESSAGE) {
      handleChatNotification(notification);
      return;
    }
    
    // Handle different notification types based on user role
    if (user?.category === 'candidate') {
      console.log("Candidate notification clicked, navigating to candidate dashboard");
      
      // Trigger specific events based on notification type
      if (notification.type === NotificationType.OFFER_PLACED) {
        const refreshEvent = new CustomEvent('refreshApplications');
        window.dispatchEvent(refreshEvent);
      } else if (notification.type === NotificationType.HIRED) {
        const confettiEvent = new CustomEvent('showConfetti');
        window.dispatchEvent(confettiEvent);
        const refreshEvent = new CustomEvent('refreshApplications');
        window.dispatchEvent(refreshEvent);
      } else if (notification.type === NotificationType.SHORTLISTED_BY_RECRUITER || 
                 notification.type === NotificationType.SHORTLISTED_BY_MANAGER || 
                 notification.type === NotificationType.REJECTED_BY_MANAGER) {
        const refreshEvent = new CustomEvent('refreshApplications');
        window.dispatchEvent(refreshEvent);
      }
      
      // Navigate to candidate dashboard
      navigate(ROUTES.CANDIDATE_DASHBOARD);
    } else if (user?.category === 'recruiter') {
      console.log("Recruiter notification clicked, navigating to jobs dashboard");
      
      // For recruiters, navigate to their jobs page
      navigate(ROUTES.JOBS);
    } else if (user?.category === 'manager') {
      console.log("Manager notification clicked, navigating to manager dashboard");
      
      // For managers, navigate to their dashboard
      navigate(ROUTES.DASHBOARD);
    } else {
      console.log("Unknown user type notification clicked - no navigation needed");
    }
  };

  const handleChatNotification = (notification: Notification) => {
    console.log("Chat message notification clicked:", notification);
    
    // Extract conversation details from notification data
    const data = notification.data || {};
    const { chatType, jobApplicationId, candidateId, senderName, jobTitle, companyName, jobId } = data;
    
    console.log("Chat notification data:", { chatType, jobApplicationId, candidateId, senderName, jobTitle, companyName, jobId });
    
    // Navigate directly to the specific job and open the chat
    const currentPath = window.location.pathname;
    
    if (chatType === 'recruiter-candidate') {
      // Navigate to recruiter dashboard for this specific job
      if (jobId && !currentPath.includes(`/recruiter/jobs/${jobId}/applications`)) {
        console.log("Navigating to specific job applications page:", `/recruiter/jobs/${jobId}/applications`);
        
        // Store the jobApplicationId to open chat after page loads
        sessionStorage.setItem('openChatOnLoad', JSON.stringify({
          jobApplicationId,
          chatType,
          candidateId,
          senderName,
          jobTitle
        }));
        
        // Use React Router navigation instead of window.location.href
        navigate(`/recruiter/jobs/${jobId}/applications`);
      } else {
        // Already on the right page, just open the chat
        const chatEvent = new CustomEvent('openChat', {
          detail: {
            chatType,
            jobApplicationId,
            candidateId,
            senderName,
            jobTitle,
            notificationId: notification._id
          }
        });
        window.dispatchEvent(chatEvent);
      }
    } else if (chatType === 'recruiter-manager') {
      // For manager notifications, navigate to manager dashboard
      if (!currentPath.includes('/manager')) {
        console.log("Navigating to manager dashboard");
        
        // Store the jobApplicationId to open chat after page loads
        sessionStorage.setItem('openChatOnLoad', JSON.stringify({
          jobApplicationId,
          chatType: 'recruiter-manager',
          senderName,
          jobTitle
        }));
        
        // Use React Router navigation instead of window.location.href
        navigate(ROUTES.DASHBOARD);
      } else {
        // Already on manager page, just open the chat
        const chatEvent = new CustomEvent('openChat', {
          detail: {
            chatType,
            jobApplicationId,
            senderName,
            jobTitle,
            notificationId: notification._id
          }
        });
        window.dispatchEvent(chatEvent);
      }
    }
  };

  return (
    <Dropdown
      open={visible}
      onOpenChange={handleOpenChange}
      overlay={
        <div className="notification-dropdown" style={{ width: 350, maxHeight: 400, overflow: "auto" }}>
          <NotificationList 
            notifications={notifications} 
            onMarkAsRead={markAsRead} 
            onNotificationClick={handleNotificationClick}
          />
        </div>
      }
      trigger={["click"]}
      placement="bottomRight"
    >
      <div className="notification-bell-container">
        <Badge count={unreadCount} className="notification-badge">
          <BellOutlined className="anticon-bell" />
        </Badge>
      </div>
    </Dropdown>
  );
};
